import { z } from "zod";

/** Backend error code when LiveKit is not configured (e.g. HTTP 503). */
export const LIVEKIT_NOT_CONFIGURED_CODE = "LIVEKIT_NOT_CONFIGURED" as const;

const liveKitTokenResponseSchema = z.object({
  token: z.string(),
});

const apiErrorBodySchema = z.object({
  error: z
    .object({
      code: z.string().optional(),
      message: z.string().optional(),
    })
    .optional(),
});

export type FetchLiveKitTokenParams = {
  roomName: string;
  /**
   * LiveKit participant identity. Prefer the authenticated user id so the issued JWT matches backend policy.
   */
  identity: string;
  accessToken: string;
};

export type FetchLiveKitTokenEnv = {
  apiUrl: string;
  isDevMode: boolean;
  /** When set in dev mode, skip API call and return this token (local shortcut only). */
  devBypassToken?: string | undefined;
};

export type FetchLiveKitTokenError = {
  ok: false;
  status: number;
  code?: string;
  message: string;
  livekitNotConfigured: boolean;
};

export type FetchLiveKitTokenResult =
  | { ok: true; token: string }
  | FetchLiveKitTokenError;

/**
 * Builds the GET URL for the LiveKit access token under `/api/v1/livekit/token`.
 * Query: `room`, `identity` (backend contract; WebSocket `voice.start` still uses `room_name`).
 */
export function buildLiveKitTokenUrl(
  apiUrl: string,
  params: { roomName: string; identity: string },
): string {
  const url = new URL(`${apiUrl}/api/v1/livekit/token`);
  url.searchParams.set("room", params.roomName);
  url.searchParams.set("identity", params.identity);
  return url.toString();
}

async function parseApiError(response: Response): Promise<{
  code?: string;
  message: string;
}> {
  try {
    const json: unknown = await response.json();
    const parsed = apiErrorBodySchema.safeParse(json);
    if (parsed.success && parsed.data.error) {
      const { code, message } = parsed.data.error;
      return {
        code,
        message: message?.trim() || response.statusText || "Request failed",
      };
    }
  } catch {
    // non-JSON body
  }
  return {
    message: response.statusText || "Request failed",
  };
}

/**
 * Fetches a LiveKit room JWT from the REST API (`GET /api/v1/livekit/token`).
 */
export async function fetchLiveKitToken(
  params: FetchLiveKitTokenParams,
  env: FetchLiveKitTokenEnv,
): Promise<FetchLiveKitTokenResult> {
  const devToken = env.devBypassToken;
  if (env.isDevMode && typeof devToken === "string" && devToken.length > 0) {
    return { ok: true, token: devToken };
  }

  const url = buildLiveKitTokenUrl(env.apiUrl, {
    roomName: params.roomName,
    identity: params.identity,
  });

  let response: Response;
  try {
    response = await fetch(url, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${params.accessToken}`,
        Accept: "application/json",
      },
    });
  } catch {
    return {
      ok: false,
      status: 0,
      message: "Network error",
      livekitNotConfigured: false,
    };
  }

  if (!response.ok) {
    const { code, message } = await parseApiError(response);
    const livekitNotConfigured =
      response.status === 503 && code === LIVEKIT_NOT_CONFIGURED_CODE;
    return {
      ok: false,
      status: response.status,
      code,
      message,
      livekitNotConfigured,
    };
  }

  let json: unknown;
  try {
    json = await response.json();
  } catch {
    return {
      ok: false,
      status: response.status,
      message: "Invalid token response",
      livekitNotConfigured: false,
    };
  }

  const parsed = liveKitTokenResponseSchema.safeParse(json);
  if (!parsed.success) {
    return {
      ok: false,
      status: response.status,
      message: "Invalid token response",
      livekitNotConfigured: false,
    };
  }

  return { ok: true, token: parsed.data.token };
}
